import { Theme, ThemeProvider, useTheme } from '@mui/material'
import { GlobalStyles } from '@mui/styled-engine'
import makeStyles from '@mui/styles/makeStyles'
import { percent } from 'csx'
import { useMemo } from 'react'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router'
import { LinkRouter } from '../../embedded/link/LinkRouter'
import { FormBuilderTest } from '../../modules/FormBuilderInterop/FormBuilderTest/TestFile'
import { WorkflowEditorHostRouter } from '../../modules/Workflows/WorkflowEditorHostRouter'
import { HomePage } from '../../pages/HomePage'
import LoginPage from '../../pages/LoginPage'
import { AuthenticationProviderLoginPage } from '../../pages/LoginPage/AuthenticationProviderLoginPage'
import { ForgotPasswordPage } from '../../pages/LoginPage/ForgotPasswordPage'
import { LoginAuthMethodsPage } from '../../pages/LoginPage/LoginAuthMethodsPage'
import { LogoutPage } from '../../pages/LoginPage/LogoutPage'
import { ResetPasswordPage } from '../../pages/LoginPage/ResetPasswordPage'
import { InvoiceOcrDemoPage } from '../../pages/OcrDemo/InvoiceOcrDemoPage'
import { OcrDemoPage } from '../../pages/OcrDemo/OcrDemoPage'
import { HTMLPortalViewer } from '../../pages/Organization/Administration/Portals/HTMLPortalViewer'
import { Eula } from '../../pages/Organization/Eula'
import OrganizationRouter from '../../pages/Organization/OrganizationRouter'
import PackageHostPage from '../../pages/PackageHostPage'
import { ReceivedFormPackageHostPage } from '../../pages/PackageHostPage/ReceivedFormPackageHostPage'
import { SavedFormPackageHostPage } from '../../pages/PackageHostPage/SavedFormPackageHostPage'
import { WorkflowTaskReviewPage } from '../../pages/WorkflowTasks/WorkflowTaskReviewPage'
import { PromptContextProvider } from '../../services/notifications/PromptService'
import { SessionService } from '../../services/session'
import { makeChildRoute } from '../../utils/CreateChildRoute'
import AnonymousRoute from '../../utils/router/AnonymousRoute'
import AuthenticatedRoute from '../../utils/router/AuthenticatedRoute'
import OrganizationRoute from '../../utils/router/OrganizationRoute'
import { ToastWrapper } from '../toast/ToastWrapper'
import { createCustomTheme } from './App'

/* Some Route Notes:
 * AnonymousRoute - for routes that should only be viewed by users that are not signed in. They
    may have an anonymous token, but don't have to. 
 * AuthenticatedRoute - for users that have a token, whether they have an anonymous one or they
		are signed in with a FIM account.
 * OrganizationRoute - for users signed into FIM
 * Route - anyone can view, whether they are signed in, anonymous, or don't have a token
 */

export function RootRouter() {
	const sessionService = SessionService.getInstance()

	const { path } = useRouteMatch()

	const styles = useStyles()

	const theme = useTheme()

	const ocrDemoTheme = useMemo(() => createCustomTheme({ mode: 'ocrDemo' }), [])

	return (
		<>
			<GlobalStyles
				styles={{
					['::-webkit-scrollbar']: {
						width: theme.spacing(1),
						height: theme.spacing(1),
					},

					['::-webkit-scrollbar-thumb']: {
						background:
							theme.palette.mode === 'dark'
								? theme.palette.primary.dark
								: theme.palette.primary.light,
						borderRadius: theme.spacing(4),
					},

					['::-webkit-scrollbar-thumb:hover']: {
						background: theme.palette.primary.main,
					},

					['::-webkit-scrollbar-corner']: {
						opacity: 0,
					},
				}}
			/>
			<div className={styles.appRoot}>
				<Switch>
					{/* <Route path={makeChildRoute('_ocr/:workflowTaskId?', path)}>
						<OcrDocumentReviewDemo />
					</Route> */}
					<AuthenticatedRoute
						path={makeChildRoute('_external/_ocr-demo', path)}
					>
						<ThemeProvider theme={ocrDemoTheme}>
							<PromptContextProvider theme={ocrDemoTheme}>
								<OcrDemoPage />
							</PromptContextProvider>
						</ThemeProvider>
					</AuthenticatedRoute>
					<AuthenticatedRoute
						path={makeChildRoute('_external/_invoice-demo', path)}
					>
						<ThemeProvider theme={ocrDemoTheme}>
							<PromptContextProvider theme={ocrDemoTheme}>
								<InvoiceOcrDemoPage />
							</PromptContextProvider>
						</ThemeProvider>
					</AuthenticatedRoute>
					<Route path={makeChildRoute('/documentation', path)} />
					<Route
						path={makeChildRoute('fbtest', path)}
						component={FormBuilderTest}
					/>
					<AuthenticatedRoute
						path={makeChildRoute('_tasks/:workflowTaskId?', path)}
						component={WorkflowTaskReviewPage}
					/>
					<OrganizationRoute
						path={makeChildRoute('_workflows/:workflowId', path)}
						component={WorkflowEditorHostRouter}
					/>
					<Route
						path={makeChildRoute('_logout', path)}
						component={LogoutPage}
					/>
					<AnonymousRoute
						path={makeChildRoute('signin-oidc/:instanceId', path)}
					>
						<AuthenticationProviderLoginPage />
					</AnonymousRoute>
					<AnonymousRoute path={makeChildRoute('_login/_auth-methods', path)}>
						<LoginAuthMethodsPage />
					</AnonymousRoute>
					<AnonymousRoute
						path={makeChildRoute('_login/:providerInstanceId(\\d+)?', path)}
					>
						{/* the regex in the above path checks that it's a number :) otherwise
						we fail to match on the auth methods path*/}
						<LoginPage />
					</AnonymousRoute>
					<AnonymousRoute path={makeChildRoute('_forgot-password', path)}>
						<ForgotPasswordPage />
					</AnonymousRoute>
					<AnonymousRoute path={makeChildRoute('ResetPassword', path)}>
						<ResetPasswordPage />
					</AnonymousRoute>
					<OrganizationRoute
						path={makeChildRoute(
							'_package-host/:packageId/_saved/:formPackageInstanceId',
							path,
						)}
					>
						<SavedFormPackageHostPage />
					</OrganizationRoute>
					<AuthenticatedRoute
						path={makeChildRoute(
							'_package-host/:packageId/_received/:workflowTaskId',
							path,
						)}
					>
						<ReceivedFormPackageHostPage />
					</AuthenticatedRoute>
					<OrganizationRoute path={makeChildRoute('_eula', path)}>
						<Eula />
					</OrganizationRoute>
					<Route path={makeChildRoute('_validate-db', path)}>
						<HomePage />
					</Route>
					{/* signed in user route */}
					<AuthenticatedRoute
						path={makeChildRoute('_package-host/:packageId', path)}
					>
						<PackageHostPage />
					</AuthenticatedRoute>

					<AuthenticatedRoute path={makeChildRoute('_external/_link/', path)}>
						<LinkRouter />
					</AuthenticatedRoute>

					<OrganizationRoute
						path={[
							makeChildRoute(
								'_portals/:portalId/_versions/:versionNumber',
								path,
							),
							makeChildRoute('_portals/:portalId', path),
						]}
					>
						<HTMLPortalViewer />
					</OrganizationRoute>
					<AuthenticatedRoute
						path={[
							makeChildRoute('_external/_portals/:portalId', path),
							makeChildRoute(
								'_external/_portals/:portalId/_versions/:versionNumber',
								path,
							),
						]}
						render={({ match }) => {
							return sessionService.isSignedInUser ? (
								<Redirect
									to={
										match.params.versionNumber !== undefined
											? `/_portals/${match.params.portalId}/_versions/${match.params.versionNumber}`
											: `/_portals/${match.params.portalId}`
									}
								/>
							) : (
								<HTMLPortalViewer />
							)
						}}
					/>
					<Route path={makeChildRoute('/', path)}>
						{sessionService.isSignedInUser ? (
							<Redirect to={`/_user/_home`} />
						) : (
							<Redirect to={`/_login`} />
						)}
					</Route>
					{/* catches all from path "/" -- handles default "/" and takes user to /_user/_home */}
					<OrganizationRoute path={path}>
						<OrganizationRouter></OrganizationRouter>
					</OrganizationRoute>
				</Switch>

				<ToastWrapper subscriptionArea="global" />
			</div>
		</>
	)
}

const useStyles = makeStyles((theme: Theme) => ({
	appRoot: {
		display: 'flex',

		width: percent(100),
		height: percent(100),

		background: theme.palette.background.default,
	},
}))
